import React, { FC } from 'react';

import { Box, HStack, ListItem, OrderedList, Stack } from '@chakra-ui/react';

import { PlusCountTag } from 'components/DataDisplay';
import { CustomTooltip } from 'components/DataDisplay/Tooltip/CustomTooltip';
import { formatTooltip } from 'components/DataDisplay/Tooltip/utils';
import { Popover } from 'components/Overlay';

import { RoleList } from './RoleList';
import { describeGrantors, Grantors } from './utils';

interface GrantorsLineProps {
  grantors: Grantors;
  // "via 3 roles" instead of the first role's name.
  compact?: boolean;
}

/**
 * A cell's "via" line, with more than one role:
 * - compact ("via 3 roles", the Contains column): click opens the popover
 *   listing the roles, each of which opens in the drawer (as before);
 * - otherwise: hovering the "+N" tag lists the role names in a tooltip.
 */
export const GrantorsLine: FC<GrantorsLineProps> = ({ grantors, compact }) => {
  const { text, more } = describeGrantors(grantors, compact);
  if (grantors.count <= 1) return <>{text}</>;

  // A click here must not open or close the row behind it, including clicks
  // inside the popover, which React passes up through the portal.
  const stop = (e: React.MouseEvent) => e.stopPropagation();
  const unlisted = grantors.count - grantors.roles.length;

  if (compact) {
    // Restored: the Contains column's popover with clickable roles.
    return (
      <Popover
        button={
          <Box
            as="button"
            type="button"
            color="primary"
            cursor="pointer"
            _hover={{ textDecoration: 'underline' }}
            onClick={stop}
          >
            {text}
          </Box>
        }
        header="Granted via"
        body={
          <Box onClick={stop} maxH="300px" overflowY="auto">
            <RoleList
              roles={grantors.roles}
              direct={grantors.direct}
              more={unlisted}
            />
          </Box>
        }
        isLazy
        portal
        placement="bottom-start"
        styles={{ content: { w: 'auto', minW: '240px', maxW: '400px' } }}
      />
    );
  }

  // Role names in the shared tooltip format, for the +N tag.
  const tooltip = formatTooltip({
    header: 'Granted via',
    width: 48,
    content: (
      <Stack spacing={1}>
        {grantors.direct && <Box>Direct</Box>}
        <OrderedList spacing={0.5}>
          {grantors.roles.map(role => (
            <ListItem key={role.uuid}>{role.name ?? role.uuid}</ListItem>
          ))}
        </OrderedList>
        {unlisted > 0 && <Box>and {unlisted.toLocaleString()} more</Box>}
      </Stack>
    ),
  });

  return (
    <HStack spacing={1} minW={0}>
      <Box overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
        {text}
      </Box>
      {/* The +N tag keeps the default cursor since it is hover-only. */}
      <CustomTooltip label={tooltip}>
        <Box as="span" onClick={stop} flexShrink={0}>
          <PlusCountTag count={more} />
        </Box>
      </CustomTooltip>
    </HStack>
  );
};
